import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { PAGE_FEATURES } from './permissionsConfig';
import { getRulesErrorMessage } from './rolesErrors';

type MemberRole = 'owner' | 'organizer' | 'board' | 'va';

type EntityMember = {
  userId: string;
  role: MemberRole;
};

interface PermissionsMatrixProps {
  entityId: string | null;
  currentUserId: string | null | undefined;
  canManage?: boolean;
}

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Organisator',
  organizer: 'Organisator',
  board: 'Bestuurslid',
  va: 'V,A'
};

const PermissionsMatrix: React.FC<PermissionsMatrixProps> = ({ entityId, currentUserId, canManage = false }) => {
  const [members, setMembers] = useState<EntityMember[]>([]);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entityId) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const membersQuery = query(
          collection(db, 'entityMembers'),
          where('entityId', '==', entityId)
        );
        const membersSnap = await getDocs(membersQuery);
        const nextMembers = membersSnap.docs.map(docSnap => docSnap.data() as EntityMember);

        const permissionsQuery = query(
          collection(db, 'permissions'),
          where('entityId', '==', entityId)
        );
        const permissionsSnap = await getDocs(permissionsQuery);
        const nextPermissions: Record<string, boolean> = {};
        permissionsSnap.docs.forEach(docSnap => {
          const data = docSnap.data() as { userId: string; page: string; feature: string; allowed: boolean };
          const key = `${data.userId}:${data.page}:${data.feature}`;
          nextPermissions[key] = data.allowed === true;
        });

        setMembers(nextMembers);
        setPermissions(nextPermissions);
      } catch (err) {
        console.error('Error loading permissions matrix:', err);
        setError(getRulesErrorMessage(err, 'Kan rechtenmatrix niet laden.'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [entityId]);

  const updatePermission = async (userId: string, page: string, feature: string, allowed: boolean) => {
    if (!entityId) return;
    if (!currentUserId) {
      setError('Je moet ingelogd zijn om rechten te wijzigen.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const docId = `${entityId}_${userId}_${page}_${feature}`;
      const permissionRef = doc(db, 'permissions', docId);
      const permissionSnap = await getDoc(permissionRef);

      if (permissionSnap.exists()) {
        await updateDoc(permissionRef, { allowed, updatedAt: serverTimestamp() });
      } else {
        await setDoc(permissionRef, {
          entityId,
          userId,
          page,
          feature,
          allowed,
          createdAt: serverTimestamp(),
          createdBy: currentUserId
        });
      }

      setPermissions(prev => ({ ...prev, [`${userId}:${page}:${feature}`]: allowed }));
    } catch (err) {
      console.error('Error updating permission:', err);
      setError(getRulesErrorMessage(err, 'Je hebt geen bevoegdheid om dit recht te wijzigen.'));
    } finally {
      setSaving(false);
    }
  };

  if (!entityId) {
    return <p className="text-sm text-gray-600">Selecteer een entiteit om rechten te beheren.</p>;
  }

  if (loading) {
    return <p className="text-sm text-gray-600">Rechtenmatrix laden...</p>;
  }

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-[#4F6660]">{error}</p>}
      {!canManage && (
        <p className="text-sm text-gray-600">Je hebt geen bevoegdheid om rechten te wijzigen.</p>
      )}
      {members.length === 0 && <p className="text-sm text-gray-600">Geen leden gevonden.</p>}

      {members.map(member => (
        <div key={member.userId} className="border border-gray-200 rounded-lg p-4">
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-900">Gebruiker: {member.userId}</p>
            <p className="text-xs text-gray-600">Rol: {ROLE_LABELS[member.role]}</p>
          </div>

          <div className="space-y-3">
            {Object.entries(PAGE_FEATURES).map(([pageId, config]) => (
              <div key={pageId} className="border border-gray-100 rounded-md p-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  {config.label}
                </p>
                <div className="flex flex-wrap gap-4">
                  {config.features.map(feature => {
                    const key = `${member.userId}:${pageId}:${feature.id}`;
                    const checked = permissions[key] === true;
                    return (
                      <label key={feature.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => updatePermission(member.userId, pageId, feature.id, e.target.checked)}
                          disabled={saving || !canManage}
                        />
                        {feature.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PermissionsMatrix;
